import { Component, EventEmitter, Input, Output, OnChanges, SimpleChanges } from '@angular/core';
import { I18nService } from 'i18n/i18n.service';
import { IItemTreeview } from '../typings/IItemTreeview';
import { AIForProcessesService } from '../services/ai-for-processes.service';
import { ObjectType } from '../typings/ObjectType';
import { NodeConfig } from '../typings/NodeConfig';
import { IAIBpmKnowledgeBase, ItemStateEnum } from '../interfaces/ai-bpm-knowledge-base';
import { IWorkerConfiguration } from '../interfaces/base-knowledge-catalog';
import { Observable } from 'rxjs/Observable';
import { map } from 'rxjs/internal/operators/map';

@Component({
  selector: 'biz-processes-tree-view',
  templateUrl: './processes-tree-view.component.html',
  styleUrls: ['./processes-tree-view.component.scss']
})

export class ProcessesTreeViewComponent implements OnChanges {
  isCollapsed = false;
  @Input() value?: IItemTreeview;
  @Input() applications: any[] = [];
  @Output() collapseChange = new EventEmitter<boolean>();
  @Output() selectedNode = new EventEmitter<IItemTreeview>();

  nodes: IItemTreeview[] = [];

  constructor(
    private _i18nService: I18nService,
    private _aiForProcessesService: AIForProcessesService) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['applications'] && this.applications?.length > 0) {
      this.processApplications();
    }
  }

  private processApplications(): void {
    if (!this.applications?.length) return;

    this.nodes = this.createTreeNodes({
      items: this.applications,
      type: ObjectType.ROOT,
      childrenKey: 'Categories',
      nextType: ObjectType.CATEGORY
    });

    this.assignLevel(this.nodes);

    if (this.nodes.length > 0) {
      this.nodes[0].isSelected = true;
      this.onSelect(this.nodes[0]);
    }
  }

  onSelect(node: IItemTreeview) {
    this.collapseOtherNodes(node);
    this.loadAndMergeNode$(node).subscribe(updated => this.selectedNode.emit(updated));
  }

  onNodeToggled(node: IItemTreeview) {
    // No need to load data when just toggling expansion state
  }

  toggleCollapse() {
    this.isCollapsed = !this.isCollapsed;
    this.collapseChange.emit(this.isCollapsed);
  }

  private loadAndMergeNode$(node: IItemTreeview): Observable<IItemTreeview> {
      return this._aiForProcessesService.getSelectedItem(node.id).pipe(
        map(response => {
          node.aIBpmKnowledgeBase = this.mergeKnowledgeBase(node, response.aIBpmKnowledgeBase);
          node.aIWorkerConfiguration = this.mergeWorkerConfiguration(node, response.aIWorkerConfiguration);
          node.hasOverride = response.hasOverride;
          return node;
        })
      );
  }

  private mergeKnowledgeBase(node: IItemTreeview, currentKnowledgeBase: IAIBpmKnowledgeBase): IAIBpmKnowledgeBase {
    const parentKnowledgeBase = node.parent?.aIBpmKnowledgeBase;

    if (!parentKnowledgeBase) {
      node.inheritanceInfo = {
        inheritedCategories: new Map<string, { parentName: string; }>(),
        inheritedCatalogs: new Map<string, { parentName: string; }>()
      };
      return currentKnowledgeBase;
    }

    const currentCategoryMap = new Map(
      currentKnowledgeBase.kbCategories?.map(item => [item.idParent, item]) || []
    );
    const currentCatalogMap = new Map(
      currentKnowledgeBase.kbCatalogs?.map(item => [item.idParent, item]) || []
    );

    const inheritedCategories = new Map<string, { parentName: string; }>();
    const inheritedCatalogs = new Map<string, { parentName: string; }>();

    // Merge categories
    const mergedCategories = parentKnowledgeBase.kbCategories?.map(parentItem => {
      const currentItem = currentCategoryMap.get(parentItem.idParent);

      if (currentItem) {
        return currentItem;
      } else {
        // Get the source name: if parent is inheriting, use the same source, otherwise use parent's name
        const sourceName = node.parent?.inheritanceInfo?.inheritedCategories.get(parentItem.idParent)?.parentName
          || this.getNodeDisplayName(node.parent) || 'Unknown';

        inheritedCategories.set(parentItem.idParent, {
          parentName: sourceName
        });
        return parentItem;
      }
    }) || [];

    // Add categories that only exist in current data
    currentKnowledgeBase.kbCategories?.forEach(currentItem => {
      const existsInParent = parentKnowledgeBase.kbCategories?.some(
        parentItem => parentItem.idParent === currentItem.idParent
      );

      if (!existsInParent) {
        mergedCategories.push(currentItem);
      }
    });

    // Merge catalogs
    const mergedCatalogs = parentKnowledgeBase.kbCatalogs?.map(parentItem => {
      const currentItem = currentCatalogMap.get(parentItem.idParent);

      if (currentItem) {
        return currentItem;
      } else {
        // Mark as inherited if catalog is in 'set' or 'unset' state
        if (parentItem.state === ItemStateEnum.SET || parentItem.state === ItemStateEnum.UNSET) {
          // Get the source name: if parent is inheriting, use the same source, otherwise use parent's name
          const sourceName = node.parent?.inheritanceInfo?.inheritedCatalogs.get(parentItem.idParent)?.parentName
            || this.getNodeDisplayName(node.parent) || 'Unknown';

          inheritedCatalogs.set(parentItem.idParent, {
            parentName: sourceName
          });
        }
        return parentItem;
      }
    }) || [];

    // Add catalogs that only exist in current data
    currentKnowledgeBase.kbCatalogs?.forEach(currentItem => {
      const existsInParent = parentKnowledgeBase.kbCatalogs?.some(
        parentItem => parentItem.idParent === currentItem.idParent
      );

      if (!existsInParent) {
        mergedCatalogs.push(currentItem);
      }
    });

    // Save inheritance information
    node.inheritanceInfo = {
      inheritedCategories,
      inheritedCatalogs
    };

    return {
      kbCategories: mergedCategories,
      kbCatalogs: mergedCatalogs
    };
  }

  private mergeWorkerConfiguration(node: IItemTreeview, currentWorkerConfiguration: IWorkerConfiguration): IWorkerConfiguration | null {
    const parentWorkerConfiguration = node.parent?.aIWorkerConfiguration;

    // If current node has configuration, use it and don't mark as inherited
    if (currentWorkerConfiguration) {
      // Initialize inheritance info if not exists
      if (!node.inheritanceInfo) {
        node.inheritanceInfo = {
          inheritedCategories: new Map<string, { parentName: string; }>(),
          inheritedCatalogs: new Map<string, { parentName: string; }>()
        };
      }
      return currentWorkerConfiguration;
    }

    // If no configuration in current node and no parent configuration, return null
    if (!parentWorkerConfiguration) {
      // Initialize inheritance info if not exists
      if (!node.inheritanceInfo) {
        node.inheritanceInfo = {
          inheritedCategories: new Map<string, { parentName: string; }>(),
          inheritedCatalogs: new Map<string, { parentName: string; }>()
        };
      }
      return null;
    }

    // Use parent's configuration and mark as inherited
    // Get the source name: if parent is inheriting, use the same source, otherwise use parent's name
    const sourceName = node.parent?.inheritanceInfo?.inheritedWorkerConfiguration?.parentName
      || node.parent?.displayName || 'Unknown';

    // Initialize inheritance info if not exists
    if (!node.inheritanceInfo) {
      node.inheritanceInfo = {
        inheritedCategories: new Map<string, { parentName: string; }>(),
        inheritedCatalogs: new Map<string, { parentName: string; }>()
      };
    }

    // Mark worker configuration as inherited
    node.inheritanceInfo.inheritedWorkerConfiguration = {
      isInherited: true,
      parentName: sourceName
    };

    return parentWorkerConfiguration;
  }

  private createTreeNodes(config: NodeConfig, parent?: IItemTreeview): IItemTreeview[] {
    return config.items?.map(item => {
      const node = this.createNode(item, config.type);
      node.parent = parent; // <-- Assign the parent

      // Handle children based on the current type and what children exist
      if (config.type === ObjectType.CATEGORY) {
        // Categories can have both Categories and Processes as children
        let childNodes: IItemTreeview[] = [];
        
        // Add subcategories if they exist
        if (item.Categories && item.Categories.length > 0) {
          const subCategories = this.createTreeNodes({
            items: item.Categories,
            type: ObjectType.CATEGORY,
            childrenKey: 'Categories', // Categories can have more categories
            nextType: ObjectType.CATEGORY
          }, node);
          childNodes = childNodes.concat(subCategories);
        }
        
        // Add processes if they exist
        if (item.Processes && item.Processes.length > 0) {
          const processes = this.createTreeNodes({
            items: item.Processes,
            type: ObjectType.PROCESS,
            childrenKey: 'Versions',
            nextType: ObjectType.VERSION
          }, node);
          childNodes = childNodes.concat(processes);
        }
        
        node.nodes = childNodes;
      } else if (config.childrenKey && item[config.childrenKey]?.length > 0 && config.nextType) {
        // For other types, use the original logic
        node.nodes = this.createTreeNodes({
          items: item[config.childrenKey],
          type: config.nextType,
          ...this.getNextLevelConfig(config.nextType)
        }, node); // <-- Pass the current node as parent
      }

      return node;
    }) || [];
  }

  private getNextLevelConfig(currentType: ObjectType): Partial<NodeConfig> {
    const configs = {
      [ObjectType.ROOT]: { childrenKey: 'Categories', nextType: ObjectType.CATEGORY },
      [ObjectType.CATEGORY]: { 
        // Categories are now handled specially in createTreeNodes
        // This is kept for compatibility but won't be used for categories
        childrenKey: 'Processes', 
        nextType: ObjectType.PROCESS 
      },
      [ObjectType.PROCESS]: { childrenKey: 'Versions', nextType: ObjectType.VERSION },
      [ObjectType.VERSION]: { childrenKey: 'Activities', nextType: ObjectType.ACTIVITY }
    };

    return configs[currentType] || {};
  }

  private createNode(element: any, originalType: ObjectType): IItemTreeview {
    let resolvedType = originalType;

    if (originalType === ObjectType.ACTIVITY) {
      if (element.ElementType === 13)
        resolvedType = ObjectType.ACTIVITY;
      else if (element.ElementType === 15)
        resolvedType = ObjectType.SUBPROCESS;
    }

    return {
      id: element.Id,
      displayName: element.DisplayName,
      type: resolvedType,
      isSelected: false,
      isLocalizable: false,
      isExpanded: false,
      isChecked: false,
      nodes: [],
      level: 0,
      topSticky: 0
    };
  }

  private assignLevel(nodes: IItemTreeview[], level: number = 0, topOffset: number = 0) {
    const headerHeight = 34;
    nodes.forEach(node => {
      node.level = level;
      node.topSticky = topOffset;
      if (node.nodes?.length) {
        this.assignLevel(node.nodes, level + 1, topOffset + headerHeight);
      }
    });
  }

  private collapseOtherNodes(node: IItemTreeview){
    var rootNode=this.findRootNode(node);
    this.nodes.filter(n =>(rootNode.id != n.id && n.isExpanded))
              .forEach(n=>n.isExpanded = false);
  }

  private findRootNode(node:IItemTreeview):IItemTreeview{
    if(node.level==0 || node.parent==undefined)
      return node;    
     return this.findRootNode(node.parent);
  }

  private getNodeDisplayName(node: IItemTreeview): string {
    if (node.type === ObjectType.VERSION) {
      return `${this._i18nService.instant('AIFORPROCESSES.OBJECTTYPE.VERSION')} ${node.displayName}`;
    }
    return node.displayName;
  }
}
